import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import type { Tables } from '@jeraisonne/db';
import { supabase } from './supabase';

export type Profile = Tables<'profiles'>;

/** Cause d'un échec de chargement du profil — `null` = aucun échec. */
export type ProfileLoadError = 'offline' | 'unknown';

export interface AuthState {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  profileLoading: boolean;
  /** Renseigné quand le dernier fetch du profil a échoué (réseau / serveur). */
  profileError: ProfileLoadError | null;
  refreshProfile: () => Promise<void>;
}

const AuthCtx = createContext<AuthState>({
  session: null,
  user: null,
  profile: null,
  loading: true,
  profileLoading: false,
  profileError: null,
  refreshProfile: async () => {},
});

type FetchProfileResult = { profile: Profile | null; error: ProfileLoadError | null };

async function fetchProfile(userId: string): Promise<FetchProfileResult> {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle();
    if (error) throw error;
    return { profile: data ?? null, error: null };
  } catch (err) {
    console.error('fetchProfile failed', { userId, err });
    // Un profil absent (data=null) est un cas normal géré plus haut ; ici on est
    // sur une vraie erreur. `navigator.onLine === false` ⇒ pas de réseau.
    const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
    return { profile: null, error: offline ? 'offline' : 'unknown' };
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState<ProfileLoadError | null>(null);

  const sessionUserId = session?.user?.id;
  const refreshProfile = useCallback(async () => {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    // Hors ligne, getUser() ne peut pas valider le JWT et renvoie user=null :
    // on retombe sur la session locale pour ne pas éjecter un profil existant.
    const userId = user?.id ?? sessionUserId;
    if (!userId) {
      setProfile(null);
      setProfileError(null);
      return;
    }
    setProfileLoading(true);
    const { profile: p, error } = await fetchProfile(userId);
    setProfile(p);
    setProfileError(error);
    setProfileLoading(false);
  }, [sessionUserId]);

  // Effect 1 — sync only: update session state.
  // The callback MUST NOT await anything that calls supabase.from() or
  // auth.getSession(), because onAuthStateChange fires from inside
  // initialize() while initializePromise is still pending → deadlock.
  useEffect(() => {
    let mounted = true;

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, s) => {
      if (!mounted) return;
      setSession(s);
      if (!s) {
        setProfile(null);
        setProfileError(null);
      }
      setLoading(false);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // Effect 2 — fetch profile once initializePromise is resolved.
  // Runs after React processes the session state update, by which point
  // the auth lock is released and supabase.from() can proceed normally.
  useEffect(() => {
    if (!sessionUserId) return;
    let mounted = true;
    setProfileLoading(true);
    fetchProfile(sessionUserId).then(({ profile: p, error }) => {
      if (!mounted) return;
      setProfile(p);
      setProfileError(error);
      setProfileLoading(false);
    });
    return () => {
      mounted = false;
    };
  }, [sessionUserId]);

  return (
    <AuthCtx.Provider
      value={{
        session,
        user: session?.user ?? null,
        profile,
        loading,
        profileLoading,
        profileError,
        refreshProfile,
      }}
    >
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth(): AuthState {
  return useContext(AuthCtx);
}

export function isAdmin(profile: Profile | null, email?: string | null): boolean {
  return profile?.role === 'admin' || email === 'philippe.hoton@gmail.com';
}
